import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useHistory } from 'react-router-dom';

import Button from 'components/Button';
import OTPInput from 'components/forms/OTPInput';
import Heading from 'components/Heading';
import * as iconLibrary from 'components/Icons';
import Logo from 'components/Logo';
import Paragraph from 'components/Paragraph';
import LoadingScreen from 'screens/LoadingScreen';
import { apiInstance } from 'services';

import { useTheme } from '../../context/theme-context';

import styles from './VerifyEmailScreen.module.scss';

const APIService = apiInstance();
const {
  namespaces: { onboarding }
} = APIService;

const VerifyEmailScreen = ({ location: { state } }) => {
  const [checkingOTP, setCheckingOTP] = useState(false);
  const [otp, setOtp] = useState(false);
  const [verified, setVerified] = useState(false);
  const history = useHistory();
  const { getCurrentTheme } = useTheme();
  const { homepage_logo } = getCurrentTheme();

  const tokenPrefix = 'cureoscity_password_reset_';

  const handleOTPSubmit = (otpValue) => {
    setOtp(otpValue);
    setCheckingOTP(true);
  };

  useEffect(() => {
    const token1 = localStorage.getItem(`${tokenPrefix}token1`);

    if (!onboarding.isResetTokenValid(token1)) {
      history.push('/forgot');
    } else if (otp.length) {
      onboarding
        .verifyPasswordReset(token1, Number(otp))
        .then((token2) => {
          localStorage.setItem(`${tokenPrefix}token2`, token2);
          setVerified(true);
        })
        .catch(() => setVerified(false))
        .finally(() => {
          setCheckingOTP(false);
        });
    }
  }, [otp]);

  if (checkingOTP) {
    return (
      <LoadingScreen
        heading="Your email is being verified"
        description="Please wait this could take a couple of minutes."
        appearance="h2"
      />
    );
  }

  const success = {
    icon: 'CircleTick',
    heading: 'Good news!',
    text: 'Your email has been successfully verified',
    buttonText: 'Continue',
    onClick: () => {
      history.push('/reset-password');
    }
  };

  const fail = {
    icon: 'CircleExclamation',
    heading: 'Something went wrong!',
    text: 'You have entered the wrong code',
    buttonText: 'Try again',
    onClick: () => {
      setOtp(false);
    }
  };

  const verifyCode = () => {
    const verifyCheck = verified ? success : fail;

    const { heading, text, buttonText, onClick, icon } = verifyCheck;

    const Icon = iconLibrary[`Icon${icon}`];

    return (
      <div className={styles.wrapper}>
        <div className={styles['icon-wrapper']}>
          <Icon />
        </div>
        <Heading appearance="h4">{heading}</Heading>
        <Paragraph centered className={styles.paragraph}>
          {text}
        </Paragraph>

        <Button
          text={buttonText}
          appearance="primary"
          size="full-width"
          width="100%"
          {...{ onClick }}
        />
      </div>
    );
  };

  const verifyOTP = () => (
    <>
      <Heading>Verify your email</Heading>
      <Paragraph centered>
        Enter the 4 digit verification code we sent to
        <br />
        <span className={styles.email}>{state?.emailAddress}</span>
      </Paragraph>
      <div className={styles.otp}>
        <OTPInput
          onSubmit={handleOTPSubmit}
          description="Please enter the four digit passcode"
        />
      </div>

      <Paragraph className={`${styles['sub-option']} ${styles.first}`}>
        Didn&apos;t get the email?
        <Button
          size="inline-small"
          text="Resend"
          appearance="text"
          onClick={() => {
            history.push({
              pathname: '/forgot',
              state: { resendEmail: true, emailAddress: state?.emailAddress }
            });
          }}
        />
      </Paragraph>
      <Paragraph className={styles['sub-option']}>
        Wrong email address?
        <Button
          size="inline-small"
          text="Change"
          appearance="text"
          onClick={() => {
            history.push('/forgot');
          }}
        />
      </Paragraph>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Verify Email</title>
      </Helmet>
      <div className={styles.logo}>
        <Logo size="large" url={homepage_logo} altText="logo" />
      </div>
      <div className={styles.wrapper}>{!otp ? verifyOTP() : verifyCode()}</div>
    </>
  );
};

export default VerifyEmailScreen;
